import type { NoddiAsyncError } from 'noddi-types';
import { useAuthContext } from 'noddi-web';
import type { ReactNode } from 'react';
import { MissingProfileAddressCard } from '../components/Booking/MissingProfileAddressCard';
import { isProfileAddressBlocked } from '../utils/stationaryAddressGate';
import { useIsUserGroupMember } from './useIsUserGroupMember';
import { useUserGroupHasAddresses } from './useUserGroupHasAddresses';

type Args = {
  userGroupId: number | null | undefined;
  /** When false, skip fetch and treat address as satisfied. */
  required: boolean;
  countryCode?: string;
  onSaved?: () => void;
};

/**
 * Single owner for the stationary profile-address gate:
 * fetch, block CTAs, and optional MissingProfileAddressCard node.
 */
export function useProfileAddressGate({ userGroupId, required: requiredWhenLoggedIn, countryCode, onSaved }: Args): {
  blocked: boolean;
  isLoading: boolean;
  error: NoddiAsyncError | null;
  hasAddress: boolean;
  addressSection: ReactNode;
} {
  const { isLoggedIn, userData } = useAuthContext();
  const isMemberOfPayerGroup = useIsUserGroupMember(userGroupId);
  // Public pages (/b/:slug, payment info, POS) render this for anonymous visitors, who can
  // neither read nor save user-group addresses — never fetch, block or prompt them.
  // Same for a signed-in user who is not a member of the payer group, such as a
  // staff-created booking: both the read and the save 403.
  // A token login on mount leaves `isLoggedIn` true while `userData` is still null; keep
  // the requirement through that window rather than reading it as "not a member".
  const isSessionLoaded = userData != null;
  const required = requiredWhenLoggedIn && isLoggedIn && (!isSessionLoaded || isMemberOfPayerGroup);
  const resolvedUserGroupId = userGroupId ?? null;
  const { hasAddress, isLoading, error } = useUserGroupHasAddresses(required ? resolvedUserGroupId : null);

  const loading = required && isLoading;
  // Safe default: block while loading or missing so pay/confirm cannot race ahead.
  const blocked = isProfileAddressBlocked({ required, hasAddress });
  const showCard = required && !isLoading && !hasAddress && resolvedUserGroupId != null;

  const addressSection = showCard && resolvedUserGroupId != null && (
    <MissingProfileAddressCard userGroupId={resolvedUserGroupId} countryCode={countryCode} onSaved={onSaved} />
  );

  return {
    blocked,
    isLoading: loading,
    error: required ? (error ?? null) : null,
    hasAddress,
    addressSection
  };
}
